///
/// EarthBrowser
///
/// Created by Matt Giger on 11/13/2023
/// Copyright (c) 2023 Geodata Labs
///

import { requestRender } from "../engine/RenderRequest"
import { StovokorContext } from "../StovokorContext"
import { type ComponentType, type InternalFormat, type PixelFormat, type TextureFilter } from "./GLConstants"
import { adjustTextureMemory, estimateTextureBytes } from "./TextureMemory"

// Shared scratch surface for grayscale channel extraction. iOS Safari
// reclaims discarded canvas backing stores lazily (rarely at all under
// sustained WebGL load), so allocating a fresh canvas per upload piles up
// ~33MB surfaces faster than GC frees them until jetsam kills the tab.
// One shared canvas, shrunk to zero after each readback, keeps the decode
// path at a single live surface with deterministic release. Sharing is safe
// because the draw/read/upload sequence runs synchronously on the main
// thread. The R-channel output buffer is likewise reused (grow-only).
let scratchCanvas: HTMLCanvasElement | undefined
let scratchContext: CanvasRenderingContext2D | null = null
let scratchGray = new Uint8Array(0)

// Anisotropic filtering support cached per GL context: getExtension and
// especially getParameter are synchronous driver roundtrips, too expensive
// to repeat on every texture upload (tiles, UI panes, series frames)
interface AnisotropySupport {
	ext: EXT_texture_filter_anisotropic | null
	max: number
}
const anisotropyCache = new WeakMap<WebGL2RenderingContext, AnisotropySupport>()

// Whether this GL implementation accepts an image/bitmap source for an R8
// texture (format RED), letting the driver drop the other channels during
// upload. WebGL2 allows the combination, so it's expected to work; it is
// probed once per context with getError on the first attempt and the
// canvas-readback path is used if it doesn't
const directRedUploadCache = new WeakMap<WebGL2RenderingContext, boolean>()

// Read the red channel of an image into the shared scratch buffer via a 2D
// canvas; the fallback when the driver won't do the conversion itself
function extractRedChannel(context: StovokorContext, source: HTMLImageElement | ImageBitmap, width: number, height: number): Uint8Array | undefined {
	if (!scratchCanvas) {
		scratchCanvas = document.createElement('canvas')
		scratchContext = scratchCanvas.getContext('2d', { willReadFrequently: true })
	}
	const ctx = scratchContext
	if (!ctx) {
		context.logger.error('Failed to get 2D context for grayscale extraction')
		return undefined
	}
	scratchCanvas.width = width
	scratchCanvas.height = height
	ctx.drawImage(source, 0, 0)
	const rgba = ctx.getImageData(0, 0, width, height).data
	// Detach the backing store now instead of waiting for GC
	scratchCanvas.width = 0
	scratchCanvas.height = 0

	// Extract only the red channel (for grayscale images, R=G=B) into the
	// reused scratch buffer; texImage2D copies out synchronously
	if (scratchGray.length < width * height) {
		scratchGray = new Uint8Array(width * height)
	}
	const gray = scratchGray.subarray(0, width * height)
	for (let i = 0; i < width * height; i++) {
		gray[i] = rgba[i * 4]
	}
	return gray
}

function anisotropySupport(gl: WebGL2RenderingContext): AnisotropySupport {
	let entry = anisotropyCache.get(gl)
	if(!entry) {
		const ext = gl.getExtension('EXT_texture_filter_anisotropic')
		entry = { ext, max: ext ? gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number : 0 }
		anisotropyCache.set(gl, entry)
	}
	return entry
}

export interface TextureInterface {
	width: number
	height: number
	textureId: WebGLTexture | null
	minFilter: number
	magFilter: number
	wrapS: number
}

export class Texture implements TextureInterface {
	public available: boolean = false
	public width: number = 0
	public height: number = 0
	public textureId: WebGLTexture | null = null
	public minFilter: number
	public magFilter: number
	public wrapS: number
	public wrapT: number

	public internalFormat: number
	public imageFormat: number
	public dataType: number

	protected context: StovokorContext

	// Once destroyed, uploads are ignored: an in-flight loader finishing after
	// destroy() would otherwise recreate the GL texture (textureId ??=) and
	// leak it, since nothing will ever delete the resurrected texture
	private destroyed: boolean = false

	// Estimated GPU bytes currently allocated for this texture, mirrored into
	// the global TextureMemory counters on every (re)allocation and free
	private allocatedBytes: number = 0

	// Sampling parameters last sent to the GL texture object, see applyParameters
	private appliedMinFilter?: number
	private appliedMagFilter?: number
	private appliedWrapS?: number
	private appliedWrapT?: number

	// Track current loading operation for cancellation
	private currentLoad?: {
		url: string
		cancelled: boolean
	}

	constructor(
		context: StovokorContext,
		width: number = 1,
		height: number = 1,
		internalFormat: number = WebGL2RenderingContext.RGBA,
		imageFormat: number = WebGL2RenderingContext.RGBA,
		dataType: number = WebGL2RenderingContext.UNSIGNED_BYTE,
		minFilter: number = WebGL2RenderingContext.LINEAR_MIPMAP_NEAREST,
		magFilter: number = WebGL2RenderingContext.LINEAR,
		wrapS: number = WebGL2RenderingContext.CLAMP_TO_EDGE,
		wrapT: number = WebGL2RenderingContext.CLAMP_TO_EDGE
	) {
		this.context = context
		this.width = width
		this.height = height
		this.internalFormat = internalFormat
		this.imageFormat = imageFormat
		this.dataType = dataType
		this.minFilter = minFilter
		this.magFilter = magFilter
		this.wrapS = wrapS
		this.wrapT = wrapT

		if(dataType !== WebGL2RenderingContext.UNSIGNED_BYTE) {
			this.minFilter = WebGL2RenderingContext.NEAREST
			this.magFilter = WebGL2RenderingContext.NEAREST
		}

		this.initialize(width, height)
	}

	/**
	 * Update the global texture memory counters after a (re)allocation.
	 * Called after every texImage2D that (re)defines this texture's storage.
	 */
	private accountAllocation(mipmapped: boolean): void {
		const bytes = estimateTextureBytes(this.internalFormat, this.dataType, this.width, this.height, mipmapped)
		adjustTextureMemory(bytes - this.allocatedBytes, this.allocatedBytes === 0 && bytes > 0 ? 1 : 0)
		this.allocatedBytes = bytes
		// New contents on screen (every upload path ends here)
		requestRender()
	}

	/**
	 * Reads back the texture data from the GPU
	 * @returns The texture data as an appropriate typed array
	 */
	public readTextureData(): ArrayBufferView {
		if (!this.textureId || !this.available) {
			this.context.logger.warn("Cannot read texture data: texture not available")
			return new Uint8Array(0)
		}

		const gl = this.context.gl

		// Create a framebuffer to read from the texture
		const framebuffer = gl.createFramebuffer()
		gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, framebuffer)
		gl.framebufferTexture2D(
			WebGL2RenderingContext.FRAMEBUFFER,
			WebGL2RenderingContext.COLOR_ATTACHMENT0,
			WebGL2RenderingContext.TEXTURE_2D,
			this.textureId,
			0
		)

		// Check if the framebuffer is complete
		if (gl.checkFramebufferStatus(WebGL2RenderingContext.FRAMEBUFFER) !== WebGL2RenderingContext.FRAMEBUFFER_COMPLETE) {
			this.context.logger.error("Framebuffer not complete")
			gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, null)
			gl.deleteFramebuffer(framebuffer)
			return new Uint8Array(0)
		}

		// Create an appropriate buffer based on the data type
		let buffer: ArrayBufferView
		switch (this.dataType) {
			case WebGL2RenderingContext.UNSIGNED_BYTE:
				buffer = new Uint8Array(this.width * this.height * 4)
				gl.readPixels(0, 0, this.width, this.height, this.imageFormat, this.dataType, buffer)
				break
			case WebGL2RenderingContext.UNSIGNED_SHORT:
				buffer = new Uint16Array(this.width * this.height)
				gl.readPixels(0, 0, this.width, this.height, this.imageFormat, this.dataType, buffer)
				break
			case WebGL2RenderingContext.FLOAT:
				buffer = new Float32Array(this.width * this.height)
				gl.readPixels(0, 0, this.width, this.height, this.imageFormat, this.dataType, buffer)
				break
			default:
				this.context.logger.error(`Unsupported data type for readback: ${this.dataType}`)
				buffer = new Uint8Array(0)
		}

		// Clean up
		gl.bindFramebuffer(WebGL2RenderingContext.FRAMEBUFFER, null)
		gl.deleteFramebuffer(framebuffer)
		return buffer
	}

	/**
	 * Allocate (or reallocate) zeroed storage of the given size with the
	 * current formats and sampling parameters. No CPU-side pixel buffer is
	 * involved: texImage2D with null data is zero-initialized by the spec,
	 * so a 4096x4096 RGBA target no longer costs a transient 64 MB JS
	 * allocation on top of the GPU storage.
	 */
	public initialize(width: number, height: number): void {
		this.width = width
		this.height = height

		const gl = this.context.gl

		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.width, this.height, 0,
			this.imageFormat, this.dataType, null)
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.accountAllocation(false)
	}

	public destroy(): void {
		// Cancel any pending load
		if(this.currentLoad) {
			this.currentLoad.cancelled = true
			this.currentLoad = undefined
		}

		this.destroyed = true
		this.available = false
		if(this.textureId !== null) {
			this.context.glState.forgetTexture(this.textureId)
			this.context.gl.deleteTexture(this.textureId)
			this.textureId = null
		}
		if(this.allocatedBytes > 0) {
			adjustTextureMemory(-this.allocatedBytes, -1)
			this.allocatedBytes = 0
		}
		requestRender()
	}

	public async loadUrl(url: string): Promise<void> {
		// Cancel any pending load
		if(this.currentLoad) {
			this.currentLoad.cancelled = true
		}

		// Create cancellation token for this load
		const loadToken = {
			url: url,
			cancelled: false
		}
		this.currentLoad = loadToken

		this.available = false
		this.width = 0
		this.height = 0

		const image = new Image()
		image.crossOrigin = 'cors'	// or 'anonymous'
		image.src = url

		try {
			await image.decode()
		} catch {
			// Image failed to load, silently ignore
			if(this.currentLoad === loadToken) {
				this.currentLoad = undefined
			}
			return
		}

		// Check if this load was cancelled while we were loading
		if(loadToken.cancelled || this.currentLoad !== loadToken) {
			return
		}

		this.uploadCanvas(image, image.width, image.height)

		// Clear the load token if it's still ours
		if(this.currentLoad === loadToken) {
			this.currentLoad = undefined
		}
	}

	public async setFromArrayBuffer(data: Uint8Array | Uint16Array | Float32Array, type?: string): Promise<void> {
		this.available = false
		this.width = 0
		this.height = 0

		const image = new Image()
		const blob = (type !== undefined) ? new Blob([data as BlobPart], { type: type }) : new Blob([data as BlobPart])
		const url = URL.createObjectURL(blob)
		image.src = url
		await image.decode()
		URL.revokeObjectURL(url)
		this.uploadCanvas(image, image.width, image.height)
	}

	public uploadArrayBuffer(buffer: Uint8Array | Uint16Array | Float32Array, width: number, height: number, samplesPerPixel: number, bitsPerSample: number): void {
		if(this.destroyed) return
		this.width = width
		this.height = height

		const gl = this.context.gl
		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)

		switch(samplesPerPixel) {
			case 1:
				this.internalFormat = WebGL2RenderingContext.R8
				this.imageFormat = WebGL2RenderingContext.RED
				break
			case 3:
				this.internalFormat = WebGL2RenderingContext.RGB8
				this.imageFormat = WebGL2RenderingContext.RGB
				break
			case 4:
				this.internalFormat = WebGL2RenderingContext.RGBA8
				this.imageFormat = WebGL2RenderingContext.RGBA
				break
		}
	switch(bitsPerSample) {
		case 8:
			if(!(buffer instanceof Uint8Array)) {
				buffer = new Uint8Array(buffer)
			}
			this.dataType = WebGL2RenderingContext.UNSIGNED_BYTE
			if (samplesPerPixel === 1) {
				this.internalFormat = WebGL2RenderingContext.R8
			}
			this.minFilter = this.magFilter = WebGL2RenderingContext.NEAREST
			break
			case 16:
				if(!(buffer instanceof Uint16Array)) {
					buffer = new Uint16Array(buffer)
				}
				this.dataType = WebGL2RenderingContext.UNSIGNED_SHORT
				this.internalFormat = WebGL2RenderingContext.R16UI
				this.imageFormat = WebGL2RenderingContext.RED_INTEGER
				// this.minFilter = this.magFilter = WebGL2RenderingContext.NEAREST
				break
			case 32:
				if(!(buffer instanceof Float32Array)) {
					buffer = new Float32Array(buffer)
				}
				this.dataType = WebGL2RenderingContext.FLOAT
				this.internalFormat = WebGL2RenderingContext.R32F
				this.imageFormat = WebGL2RenderingContext.RED
				// this.minFilter = this.magFilter = WebGL2RenderingContext.NEAREST
				break
		}
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_ALIGNMENT, 1)

		gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.width, this.height, 0, this.imageFormat, this.dataType, buffer)
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		this.accountAllocation(false)
	}

	/**
	 * Upload an image, canvas or bitmap. Mipmaps are built by default for
	 * content that gets minified on screen; pass mipmaps=false for surfaces
	 * drawn 1:1 (UI panes rasterized at the device pixel ratio), which
	 * saves the mip-generation stall on every re-rasterization and a third
	 * of the memory. Without mip levels a mipmap min filter would sample
	 * black, so the filter drops to LINEAR in that case.
	 */
	public uploadCanvas(source: TexImageSource, width: number, height: number, mipmaps: boolean = true): void {
		if(this.destroyed) return
		const gl = this.context.gl
		this.width = width
		this.height = height
		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.imageFormat, this.dataType, source)

		if(mipmaps) {
			gl.generateMipmap(WebGL2RenderingContext.TEXTURE_2D)

			// Apply anisotropic filtering if available (support queried once per context)
			const aniso = anisotropySupport(gl)
			if(aniso.ext) {
				gl.texParameterf(WebGL2RenderingContext.TEXTURE_2D, aniso.ext.TEXTURE_MAX_ANISOTROPY_EXT, aniso.max)
			}
		} else if(Texture.isMipmapFilter(this.minFilter)) {
			this.minFilter = WebGL2RenderingContext.LINEAR
		}
		this.applyParameters()

		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		this.accountAllocation(mipmaps)
	}

	private static isMipmapFilter(filter: number): boolean {
		return filter === WebGL2RenderingContext.LINEAR_MIPMAP_NEAREST ||
			filter === WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR ||
			filter === WebGL2RenderingContext.NEAREST_MIPMAP_NEAREST ||
			filter === WebGL2RenderingContext.NEAREST_MIPMAP_LINEAR
	}

	/**
	 * Upload a decoded ImageBitmap as raw channel data: RGB8, NEAREST
	 * filtering, no mipmaps, no color management. For data encoded in image
	 * channels (e.g. 16-bit elevation packed into the R/G bytes of lossless
	 * WebP tiles) where filtering or color conversion would corrupt values.
	 */
	public uploadDataBitmap(source: ImageBitmap): void {
		if(this.destroyed) return
		const gl = this.context.gl
		this.width = source.width
		this.height = source.height
		this.internalFormat = WebGL2RenderingContext.RGB8
		this.imageFormat = WebGL2RenderingContext.RGB
		this.dataType = WebGL2RenderingContext.UNSIGNED_BYTE
		this.minFilter = WebGL2RenderingContext.NEAREST
		this.magFilter = WebGL2RenderingContext.NEAREST

		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_COLORSPACE_CONVERSION_WEBGL, WebGL2RenderingContext.NONE)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_ALIGNMENT, 1)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.imageFormat, this.dataType, source)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_COLORSPACE_CONVERSION_WEBGL, WebGL2RenderingContext.BROWSER_DEFAULT_WEBGL)
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		this.accountAllocation(false)
	}

	/**
	 * Upload an image as a single-channel R8 texture (extracts red channel only).
	 * Uses 1/4 the memory of RGBA for grayscale images.
	 *
	 * Pass mipmaps=true for high-frequency fields that get minified on screen
	 * (e.g. a 4096-wide global cloud texture viewed at globe scale), where
	 * plain LINEAR minification would shimmer; costs 33% more memory.
	 */
	public uploadGrayscale(source: HTMLImageElement, width: number, height: number, mipmaps: boolean = false): void {
		if(this.destroyed) return
		const gl = this.context.gl
		this.width = width
		this.height = height

		// Configure for R8 format
		this.internalFormat = WebGL2RenderingContext.R8
		this.imageFormat = WebGL2RenderingContext.RED
		this.dataType = WebGL2RenderingContext.UNSIGNED_BYTE

		// Single-channel overlay data is usually magnified on screen, so by
		// default skip mipmap generation (saves 33% GPU memory and an upload
		// stall) and sample with plain LINEAR - a mipmap min filter without
		// mip levels renders black
		this.minFilter = mipmaps ? WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR : WebGL2RenderingContext.LINEAR

		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_ALIGNMENT, 1)

		// Let the driver take the red channel straight from the image; fall
		// back to a canvas readback where that isn't supported
		if(!this.uploadRedFromSource(source, undefined)) {
			const gray = extractRedChannel(this.context, source, width, height)
			if(gray === undefined) {
				return
			}
			gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.width, this.height, 0,
				this.imageFormat, this.dataType, gray)
		}
		if(mipmaps) {
			gl.generateMipmap(WebGL2RenderingContext.TEXTURE_2D)
			const aniso = anisotropySupport(gl)
			if(aniso.ext) {
				gl.texParameterf(WebGL2RenderingContext.TEXTURE_2D, aniso.ext.TEXTURE_MAX_ANISOTROPY_EXT, aniso.max)
			}
		}
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		this.accountAllocation(mipmaps)
	}

	/**
	 * Copy an image source into a rectangle of this texture's existing
	 * storage (constructor / initialize), leaving the rest untouched: atlas
	 * assembly for color textures (uploadGrayscaleRegion is the R8 form).
	 * The source is drawn at its own size at (x, y); no mip levels are
	 * rebuilt, so keep a non-mipmap minification filter on atlases.
	 */
	public uploadRegion(source: TexImageSource, x: number, y: number, width: number, height: number): void {
		if(this.destroyed || !this.textureId) return
		if(x < 0 || y < 0 || x + width > this.width || y + height > this.height) {
			this.context.logger.error(`uploadRegion: ${width}x${height} at ${x},${y} exceeds ${this.width}x${this.height}`)
			return
		}
		const gl = this.context.gl
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		gl.texSubImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, x, y, width, height, this.imageFormat, this.dataType, source)
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		requestRender()
	}

	/**
	 * Copy a grayscale image's red channel into a rectangle of an existing
	 * R8 texture (allocated by the constructor / initialize with R8 storage),
	 * leaving the rest untouched. Used to assemble tiled fields into one
	 * texture; call generateMipmaps() once the assembly is complete.
	 */
	public uploadGrayscaleRegion(source: HTMLImageElement | ImageBitmap, x: number, y: number): void {
		if(this.destroyed || !this.textureId) return
		if(this.internalFormat !== WebGL2RenderingContext.R8) {
			this.context.logger.error('uploadGrayscaleRegion requires an R8 texture')
			return
		}
		const width = source.width
		const height = source.height
		if(x < 0 || y < 0 || x + width > this.width || y + height > this.height) {
			this.context.logger.error(`uploadGrayscaleRegion: ${width}x${height} at ${x},${y} exceeds ${this.width}x${this.height}`)
			return
		}
		const gl = this.context.gl

		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_ALIGNMENT, 1)
		if(!this.uploadRedFromSource(source, [x, y])) {
			const gray = extractRedChannel(this.context, source, width, height)
			if(gray === undefined) {
				this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
				return
			}
			gl.texSubImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, x, y, width, height,
				WebGL2RenderingContext.RED, WebGL2RenderingContext.UNSIGNED_BYTE, gray)
		}
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		requestRender()
	}

	/**
	 * Upload an image source's red channel into the bound R8 texture (level
	 * 0, or a sub-rectangle at `offset`). Returns false if the driver
	 * rejects image sources for RED, in which case nothing was uploaded and
	 * the caller takes the readback path. The verdict is cached per context.
	 */
	private uploadRedFromSource(source: HTMLImageElement | ImageBitmap, offset: [number, number] | undefined): boolean {
		const gl = this.context.gl
		const known = directRedUploadCache.get(gl)
		if(known === false) {
			return false
		}
		// Premultiplication would scale the red channel of any pixel with
		// alpha; raw channel data must stay as decoded
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
		if(known === undefined) {
			// Probe: drain stale errors so the verdict is about this call
			while(gl.getError() !== WebGL2RenderingContext.NO_ERROR) { /* clear */ }
		}
		if(offset === undefined) {
			gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, WebGL2RenderingContext.R8,
				WebGL2RenderingContext.RED, WebGL2RenderingContext.UNSIGNED_BYTE, source)
		} else {
			gl.texSubImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, offset[0], offset[1], source.width, source.height,
				WebGL2RenderingContext.RED, WebGL2RenderingContext.UNSIGNED_BYTE, source)
		}
		if(known === undefined) {
			const supported = gl.getError() === WebGL2RenderingContext.NO_ERROR
			directRedUploadCache.set(gl, supported)
			if(!supported) {
				this.context.logger.warn('[Texture] Direct R8 image upload unsupported; using canvas readback')
			}
			return supported
		}
		return true
	}

	/**
	 * Build the mip chain for the current level-0 contents (e.g. after
	 * rendering into this texture) and switch to trilinear minification.
	 * Re-accounts the allocation to include the mip levels.
	 */
	public generateMipmaps(): void {
		if(this.destroyed || !this.textureId) return
		const gl = this.context.gl
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		gl.generateMipmap(WebGL2RenderingContext.TEXTURE_2D)
		this.minFilter = WebGL2RenderingContext.LINEAR_MIPMAP_LINEAR
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.accountAllocation(true)
	}

	/**
	 * Set the min/mag filters, updating the GPU texture parameters if the
	 * texture has already been created
	 */
	public setFilters(minFilter: TextureFilter, magFilter: TextureFilter): void {
		this.minFilter = minFilter
		this.magFilter = magFilter

		if(this.textureId) {
			this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
			this.applyParameters()
			this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		}
	}

	/** Create the GL texture object on first use; a new object starts with no parameters applied */
	private ensureTexture(): void {
		if(this.textureId === null) {
			this.textureId = this.context.gl.createTexture()
			this.appliedMinFilter = undefined
			this.appliedMagFilter = undefined
			this.appliedWrapS = undefined
			this.appliedWrapT = undefined
		}
	}

	/**
	 * Push the sampling parameters to the bound texture, each only if it
	 * differs from what this texture object already has. Parameters are
	 * per texture object and persist across uploads, so re-sending four of
	 * them on every pane re-rasterization or tile upload was pure overhead
	 */
	private applyParameters(): void {
		const gl = this.context.gl
		if(this.appliedMinFilter !== this.minFilter) {
			gl.texParameteri(WebGL2RenderingContext.TEXTURE_2D, WebGL2RenderingContext.TEXTURE_MIN_FILTER, this.minFilter)
			this.appliedMinFilter = this.minFilter
		}
		if(this.appliedMagFilter !== this.magFilter) {
			gl.texParameteri(WebGL2RenderingContext.TEXTURE_2D, WebGL2RenderingContext.TEXTURE_MAG_FILTER, this.magFilter)
			this.appliedMagFilter = this.magFilter
		}
		if(this.appliedWrapS !== this.wrapS) {
			gl.texParameteri(WebGL2RenderingContext.TEXTURE_2D, WebGL2RenderingContext.TEXTURE_WRAP_S, this.wrapS)
			this.appliedWrapS = this.wrapS
		}
		if(this.appliedWrapT !== this.wrapT) {
			gl.texParameteri(WebGL2RenderingContext.TEXTURE_2D, WebGL2RenderingContext.TEXTURE_WRAP_T, this.wrapT)
			this.appliedWrapT = this.wrapT
		}
	}

	/**
	 * Upload raw pixel data with explicit formats, applying the current
	 * filter and wrap settings. Use this for formats uploadArrayBuffer does
	 * not cover (e.g. two-channel float data)
	 */
	public uploadRawData(buffer: ArrayBufferView, width: number, height: number, internalFormat: InternalFormat, imageFormat: PixelFormat, dataType: ComponentType): void {
		if(this.destroyed) return
		this.width = width
		this.height = height
		this.internalFormat = internalFormat
		this.imageFormat = imageFormat
		this.dataType = dataType

		const gl = this.context.gl
		this.ensureTexture()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
		this.context.glState.pixelStorei(WebGL2RenderingContext.UNPACK_ALIGNMENT, 1)
		gl.texImage2D(WebGL2RenderingContext.TEXTURE_2D, 0, this.internalFormat, this.width, this.height, 0, this.imageFormat, this.dataType, buffer)
		this.applyParameters()
		this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		this.available = true
		this.accountAllocation(false)
	}

	public setRepeating(repeat: boolean): void {
		this.wrapS = repeat ? WebGL2RenderingContext.REPEAT : WebGL2RenderingContext.CLAMP_TO_EDGE
		this.wrapT = repeat ? WebGL2RenderingContext.REPEAT : WebGL2RenderingContext.CLAMP_TO_EDGE
		if(this.textureId) {
			this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, this.textureId)
			this.applyParameters()
			this.context.glState.bindTexture(WebGL2RenderingContext.TEXTURE_2D, null)
		}
	}
}